import { useState } from 'react';
import { DEFAULT_RULESET, seedFrom, VOCATIONS, type Addons, type GameConfig } from '@estandarte/engine';
import { useGame } from '../store/game';
import { BOT_NAMES, CHRONICLES, eraRuleset, theme } from '../theme';
import { EraPicker } from './EraPicker';
import { AddonToggles, SeatRow, type SeatDraft } from './SeatRow';

let nextId = 0;
const rand = (n: number) => Math.floor(Math.random() * n);

function randomBot(used: string[], color: string): SeatDraft {
  const free = BOT_NAMES.filter((n) => !used.includes(n));
  return {
    id: `s${++nextId}`,
    name: free[rand(free.length)] ?? `Bot ${nextId}`,
    kind: 'bot',
    color,
    vocation: VOCATIONS[rand(VOCATIONS.length)],
    difficulty: 'normal',
  };
}

function initialSeats(): SeatDraft[] {
  const colors = theme.guilds.map((g) => g.color);
  const seats: SeatDraft[] = [
    { id: `s${++nextId}`, name: 'Você', kind: 'human', color: colors[0], vocation: 'defense', difficulty: 'normal' },
  ];
  for (let i = 1; i < 4; i++) seats.push(randomBot(seats.map((s) => s.name), colors[i]));
  return seats;
}

export function LocalSetup() {
  const { startLocal, go } = useGame();
  const [seats, setSeats] = useState<SeatDraft[]>(initialSeats);
  const [addons, setAddons] = useState<Addons>({ ...DEFAULT_RULESET.addons });
  const [seed, setSeed] = useState('');
  const [era, setEra] = useState<string>(CHRONICLES[0].id);
  const [advanced, setAdvanced] = useState(false);

  const humans = seats.filter((s) => s.kind === 'human').length;
  const bots = seats.length - humans;
  const names = seats.map((s) => s.name.trim().toLowerCase());
  const problems: string[] = [];
  if (seats.length < 3) problems.push('São precisos pelo menos 3 lugares.');
  if (humans < 1) problems.push('Pelo menos 1 lugar precisa ser humano.');
  if (bots > 5) problems.push('No máximo 5 bots.');
  if (names.some((n) => !n)) problems.push('Todo lugar precisa de nome.');
  if (new Set(names).size !== names.length) problems.push('Os nomes precisam ser diferentes.');

  function update(i: number, patch: Partial<SeatDraft>) {
    setSeats((list) =>
      list.map((s, k) => {
        if (k !== i) return s;
        const next = { ...s, ...patch };
        if (patch.kind === 'human' && s.kind === 'bot') next.name = `Jogador ${i + 1}`;
        if (patch.kind === 'bot' && s.kind === 'human') next.name = randomBot(list.map((x) => x.name), s.color).name;
        return next;
      }),
    );
  }

  function addSeat() {
    const taken = seats.map((s) => s.color);
    const color = theme.guilds.find((g) => !taken.includes(g.color))!.color;
    setSeats([...seats, randomBot(seats.map((s) => s.name), color)]);
  }

  function start() {
    const config: GameConfig = {
      seed: seed.trim() ? seedFrom(/^-?\d+$/.test(seed.trim()) ? Number(seed.trim()) : seed.trim()) : (Math.random() * 2 ** 31) | 0,
      content: theme.content,
      ruleset: { addons, ...eraRuleset(era) },
      seats: seats.map((s) => ({
        name: s.name.trim(),
        color: s.color,
        vocation: s.vocation,
        kind: s.kind,
        difficulty: s.difficulty,
      })),
    };
    startLocal(config);
  }

  return (
    <div className="page">
      <h1 style={{ fontSize: '2.4rem' }}>Nova partida local</h1>
      <p className="muted">
        Jogue contra bots ou passe o computador entre amigos (hot-seat). De 3 a 6 lugares, até 5 bots.
      </p>

      <div className="section-title">
        Lugares <small>{seats.length} de 6</small>
      </div>
      <div className="seats">
        {seats.map((s, i) => (
          <SeatRow
            key={s.id}
            index={i}
            seat={s}
            takenColors={seats.map((x) => x.color)}
            editable={{ name: true, kind: true, color: true, vocation: true }}
            showVocation={addons.vocations}
            onChange={(p) => update(i, p)}
            onRemove={seats.length > 3 ? () => setSeats(seats.filter((_, k) => k !== i)) : undefined}
            removeLabel={`Remover lugar ${i + 1}`}
          />
        ))}
      </div>
      <div className="row" style={{ marginTop: '0.6rem' }}>
        <button className="btn small" onClick={addSeat} disabled={seats.length >= 6}>
          + Adicionar lugar
        </button>
      </div>

      <div className="section-title">
        Era <small>cada crônica das guerras traz uma regra para a mesa</small>
      </div>
      <EraPicker value={era} onChange={setEra} bossesOn={addons.bosses} />

      <div className="section-title">Add-ons</div>
      <AddonToggles addons={addons} onChange={setAddons} />

      <div className="section-title">
        <button className="btn ghost small" onClick={() => setAdvanced(!advanced)} aria-expanded={advanced}>
          {advanced ? '▾' : '▸'} Opções avançadas
        </button>
      </div>
      {advanced && (
        <div className="field" style={{ maxWidth: 320 }}>
          <label htmlFor="seed">Seed (deixe vazio para aleatória)</label>
          <input id="seed" className="input" value={seed} onChange={(e) => setSeed(e.target.value)} placeholder="ex.: 42 ou carlin" />
        </div>
      )}

      {problems.length > 0 && (
        <div className="error-box" style={{ marginTop: '1rem' }}>
          {problems.map((p) => (
            <div key={p}>{p}</div>
          ))}
        </div>
      )}
      <div className="row" style={{ marginTop: '1.5rem' }}>
        <button className="btn" onClick={() => go('home')}>
          Voltar
        </button>
        <span className="spacer" />
        <button className="btn primary" onClick={start} disabled={problems.length > 0}>
          Começar partida
        </button>
      </div>
    </div>
  );
}
